"use client";

import React from "react";

interface LanguageFlagProps {
  language: string;
  className?: string;
  size?: number;
}

export function LanguageFlag({ language, className = "", size = 16 }: LanguageFlagProps) {
  // Map language codes to country codes for flags
  const langToCountry: Record<string, string> = {
    'en': 'gb', // English -> Great Britain
    'sv': 'se', // Swedish -> Sweden
    'da': 'dk', // Danish -> Denmark
    'no': 'no', // Norwegian -> Norway
    'fi': 'fi', // Finnish -> Finland
    'de': 'de', // German -> Germany
    'fr': 'fr', // French -> France
    'es': 'es', // Spanish -> Spain
    'it': 'it', // Italian -> Italy
    'nl': 'nl', // Dutch -> Netherlands
    'pt': 'pt', // Portuguese -> Portugal
    'pl': 'pl', // Polish -> Poland
    'ru': 'ru', // Russian -> Russia
    'zh': 'cn', // Chinese -> China
    'ja': 'jp', // Japanese -> Japan
    'ko': 'kr', // Korean -> South Korea
    'ar': 'sa', // Arabic -> Saudi Arabia
  };

  const countryCode = langToCountry[language.toLowerCase()] || language.toLowerCase();

  // Using flag-icons CSS (we'll need to install this)
  // For now, let's use inline SVG flags for the most common languages

  const flagSvgs: Record<string, React.ReactElement> = {
    'gb': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#012169" d="M0 0h640v480H0z"/>
        <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
        <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
        <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
        <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
      </svg>
    ),
    'se': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#005293" d="M0 0h640v480H0z"/>
        <path fill="#FECB00" d="M176 0v192H0v96h176v192h96V288h368v-96H272V0h-96z"/>
      </svg>
    ),
    'dk': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#c8102e" d="M0 0h640v480H0z"/>
        <path fill="#fff" d="M170 0h100v480H170z"/>
        <path fill="#fff" d="M0 190h640v100H0z"/>
      </svg>
    ),
    'no': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#ef2b2d" d="M0 0h640v480H0z"/>
        <path fill="#fff" d="M180 0h120v480H180z"/>
        <path fill="#fff" d="M0 180h640v120H0z"/>
        <path fill="#002868" d="M210 0h60v480h-60z"/>
        <path fill="#002868" d="M0 210h640v60H0z"/>
      </svg>
    ),
    'fi': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#fff" d="M0 0h640v480H0z"/>
        <path fill="#002f6c" d="M0 174.5h640v131H0z"/>
        <path fill="#002f6c" d="M175.5 0h131v480h-131z"/>
      </svg>
    ),
    'de': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#000" d="M0 0h640v160H0z"/>
        <path fill="#d00" d="M0 160h640v160H0z"/>
        <path fill="#ffce00" d="M0 320h640v160H0z"/>
      </svg>
    ),
    'fr': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#002654" d="M0 0h213.3v480H0z"/>
        <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
        <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      </svg>
    ),
    'es': (
      <svg viewBox="0 0 640 480" width={size} height={size} className={className}>
        <path fill="#c60b1e" d="M0 0h640v480H0z"/>
        <path fill="#ffc400" d="M0 120h640v240H0z"/>
      </svg>
    ),
  };

  const flag = flagSvgs[countryCode];

  if (flag) {
    return (
      <span className="inline-block" title={`Language: ${language.toUpperCase()}`}>
        {flag}
      </span>
    );
  }

  // Fallback to emoji flags or text
  const emojiFlags: Record<string, string> = {
    'gb': '🇬🇧',
    'se': '🇸🇪',
    'dk': '🇩🇰',
    'no': '🇳🇴',
    'fi': '🇫🇮',
    'de': '🇩🇪',
    'fr': '🇫🇷',
    'es': '🇪🇸',
    'it': '🇮🇹',
    'nl': '🇳🇱',
    'pt': '🇵🇹',
    'pl': '🇵🇱',
    'ru': '🇷🇺',
    'cn': '🇨🇳',
    'jp': '🇯🇵',
    'kr': '🇰🇷',
    'sa': '🇸🇦',
  };

  const emoji = emojiFlags[countryCode];

  if (emoji) {
    return (
      <span
        className={className}
        style={{ fontSize: size }}
        title={`Language: ${language.toUpperCase()}`}
      >
        {emoji}
      </span>
    );
  }

  // Final fallback to language code
  return (
    <span
      className={`inline-flex items-center justify-center rounded bg-gray-200 px-1 text-xs font-bold ${className}`}
      style={{ minWidth: size, height: size }}
      title={`Language: ${language.toUpperCase()}`}
    >
      {language.toUpperCase()}
    </span>
  );
}